import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBooking } from "@/context/BookingContext";
import GamesBento from "@/components/GamesBento";
import PullQuote from "@/components/PullQuote";
import Seo from "@/components/Seo";
import { PAGE_META, faqSchema } from "@/lib/seo";
import { SERVICES, QUOTES } from "@/data/site";
import { useSettings } from "@/context/SettingsContext";
import { Phone, Clock, MapPin, Cpu, PartyPopper, Gamepad2, ArrowRight } from "lucide-react";

const serviceIcons = [PartyPopper, Gamepad2, Cpu, PartyPopper, Gamepad2];

export default function Home() {
  const { openBooking } = useBooking();
  const { settings } = useSettings();
  const contact = settings.contact;
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY * 0.3);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        {...PAGE_META.home}
        schema={[
          faqSchema([
            {
              q: "How much does a gaming party cost in McAllen?",
              a: "RGV Gaming party packages start at $395 for a 2-hour party and $475 for 3 hours. The Gaming Fanatic package is $525 and adds 4 gaming PCs. A $150 non-refundable deposit holds your date.",
            },
            {
              q: "What areas does RGV Gaming serve?",
              a: "RGV Gaming is a mobile service based in Mercedes, TX, serving McAllen, Mercedes, Edinburg, Pharr, Weslaco, Brownsville, Harlingen and the greater Rio Grande Valley. We bring the gaming setup to your home or business.",
            },
            {
              q: "What equipment do you bring?",
              a: "We bring 10 Intel NUC i7 gaming PCs with Pixio curved monitors, 2 PlayStation 5 Slim, 2 Xbox Series X, 2 Nintendo Switch OLED and an Oculus Quest 3, all running on T-Mobile 5G business internet.",
            },
          ]),
        ]}
      />
      {/* HERO */}
      <section className="relative h-[90vh] min-h-[560px] overflow-hidden">
        <div
          className="absolute inset-0 will-change-transform"
          style={{ transform: `translateY(${offset}px) scale(1.1)` }}
        >
          <img
            src="/assets/hero-photos/trailer-wrap-hero-1500.webp"
            srcSet="/assets/hero-photos/trailer-wrap-hero-768.webp 768w, /assets/hero-photos/trailer-wrap-hero-1500.webp 1500w"
            sizes="100vw"
            width="1500"
            height="752"
            alt="RGV Gaming trailer wrapped in red and black esports branding parked outdoors"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 flex flex-col justify-center">
          <p className="text-rgv-red font-display uppercase tracking-[0.3em] text-sm sm:text-base mb-4">
            {contact.area}
          </p>
          <h1 className="font-display font-extrabold italic uppercase fluid-h1 max-w-4xl">
            RGV Gaming
            <span className="block text-rgv-red">We Bring Gaming To You</span>
          </h1>
          <p className="mt-6 text-rgv-gray text-lg max-w-xl">
            {contact.fleet}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              onClick={() => openBooking()}
              data-testid="hero-book-btn"
              className="btn-skew btn-red px-8 py-4 text-lg"
            >
              <span className="flex items-center gap-2">
                Book Your Party <ArrowRight className="h-5 w-5" />
              </span>
            </button>
            <Link to="/packages" data-testid="hero-packages-btn" className="btn-skew btn-outline-red px-8 py-4 text-lg">
              <span>View Packages</span>
            </Link>
          </div>
        </div>
      </section>

      {/* INFO BAR */}
      <div className="bg-rgv-red">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-white font-display uppercase text-base">
          <a href={`tel:${contact.phone}`} className="flex items-center justify-center gap-2">
            <Phone className="h-5 w-5" /> {contact.phone}
          </a>
          <span className="flex items-center justify-center gap-2">
            <Clock className="h-5 w-5" /> {contact.hours}
          </span>
          <span className="flex items-center justify-center gap-2">
            <MapPin className="h-5 w-5" /> Mercedes / McAllen, TX
          </span>
        </div>
      </div>

      {/* WHO WE ARE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display uppercase italic fluid-h2 mb-6">
              Who We <span className="text-rgv-red">Are</span>
            </h2>
            <p className="text-rgv-gray text-lg leading-relaxed">
              RGV Gaming brings a mobile gaming experience like no other, right to your home or
              business. Play solo or as a team across a huge lineup of multiplayer games on the
              newest hardware — PlayStation 5 Slim, Xbox Series X, Nintendo Switch OLED, and Oculus
              Quest 3. Every setup runs on Intel NUC i7 systems with Pixio curved gaming monitors,
              so it feels like a real esports lounge wherever we park. We proudly serve McAllen,
              Mercedes, Edinburg, Pharr, Weslaco, Brownsville, Harlingen and the greater Rio Grande
              Valley.
            </p>
          </div>
          <div className="relative">
            <div className="hover-glow rounded-2xl overflow-hidden border border-rgv-darkred diagonal-bottom">
              <img
                src="/assets/hero-photos/gaming-lounge-summer-camps-992.webp"
                srcSet="/assets/hero-photos/gaming-lounge-summer-camps-600.webp 600w, /assets/hero-photos/gaming-lounge-summer-camps-992.webp 992w"
                sizes="(max-width: 1024px) 100vw, 50vw"
                width="992"
                height="744"
                alt="Kids gaming in an RGV Gaming lounge with blue LED lighting"
                loading="lazy"
                decoding="async"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* GAMING PARTIES */}
      <section className="relative bg-gradient-to-br from-[#280000] to-[#0A0A0A] diagonal-top py-24 -mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display uppercase italic fluid-h2 mb-6">
            Gaming <span className="text-rgv-red">Parties</span>
          </h2>
          <p className="text-rgv-gray text-lg max-w-3xl mx-auto leading-relaxed">
            Planning a birthday party? Let us take the stress off your plate. Whether it&rsquo;s a
            Fortnite squad or a Call of Duty lobby, we bring the full setup, snacks and all, and
            handle the rest.
          </p>
          <button onClick={() => openBooking()} data-testid="parties-book-btn" className="btn-skew btn-red px-8 py-4 mt-8 text-lg">
            <span>Book Us Today</span>
          </button>
        </div>
      </section>

      {/* BOOK US FOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display uppercase italic fluid-h2 mb-10 text-center">
          Book Us <span className="text-rgv-red">For</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = serviceIcons[i % serviceIcons.length];
            return (
              <div
                key={s}
                data-testid={`service-${i}`}
                className="hover-glow border border-rgv-maroon rounded-xl bg-[#111] p-6 flex flex-col items-center text-center gap-3"
              >
                <Icon className="h-8 w-8 text-rgv-red" />
                <span className="font-display uppercase text-lg leading-tight">{s}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* GAMES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <h2 className="font-display uppercase italic fluid-h2">
            The <span className="text-rgv-red">Lineup</span>
          </h2>
          <p className="text-rgv-gray max-w-md">
            25 titles across every console and PC — from Fortnite squads to Smash Bros showdowns.
          </p>
        </div>
        <GamesBento />
      </section>

      {/* SUMMER CAMPS TEASER */}
      <section className="relative py-20 bg-gradient-to-r from-[#610008] to-[#280000] diagonal-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center gap-8 justify-between">
          <div>
            <h2 className="font-display uppercase italic fluid-h2 mb-4">
              Summer Camps
            </h2>
            <p className="text-white/90 text-lg max-w-2xl">
              We also run esports and gaming summer camps — Minecraft, Roblox, and more. Ages 7–17.
            </p>
          </div>
          <Link to="/summer-camps" data-testid="camps-teaser-btn" className="btn-skew btn-outline-red border-white text-white px-8 py-4 text-lg shrink-0">
            <span>Explore Camps</span>
          </Link>
        </div>
      </section>

      {/* CLOSING */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
        <p className="text-2xl sm:text-3xl font-display text-white leading-snug">
          No matter the game or the gamer, RGV Gaming is built for everyone. You don&rsquo;t need to be a
          &ldquo;gamer&rdquo; to have the best day of your kid&rsquo;s summer — we bring the fun to you.
        </p>
        <PullQuote quotes={QUOTES.home} />
      </section>
    </div>
  );
}
